import ButtonSecondary from "shared/Button/ButtonSecondary";
import { useMemo, useState } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/24/solid";
import { useSelector } from "react-redux";
import {
  selectEvents,
  setEvents,
} from "application/features/calendar/calendarSlice";
import { useDispatch } from "react-redux";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { selectBookings } from "application/features/bookings/bookingsSlice";
import { BookingDataType } from "data/interfaces";
import { StockEnum } from "data/enums";
import { useTranslation } from "react-i18next";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "moment/locale/it";
import "./CalendarPage.css";

moment.locale("it");
const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);

const MyCustomToolbar = (toolbar) => {
  const [view, setView] = useState("week");

  const goToToday = () => {
    // Implementa la logica per andare a "Today"
    toolbar.onNavigate("TODAY");
  };

  const goToPrev = () => {
    // Implementa la logica per andare a "Back"
    toolbar.onNavigate("PREV");
  };

  const goToNext = () => {
    // Implementa la logica per andare a "Next"
    toolbar.onNavigate("NEXT");
  };

  const changeViewTo3Days = () => {
    // Cambia la visualizzazione a 3 giorni
    setView("week");
    toolbar.onView("week");
  };

  const changeViewToWeek = () => {
    // Cambia la visualizzazione a 7 giorni
    // if screen is less than 1468px
    if (window.innerWidth < 1920) {
      alert(
        "La vista settimanale è disponibile solo su schermi con larghezza maggiore di 1920",
      );
      return;
    }
    setView("week");
    toolbar.onView("week");
  };

  return (
    <div className="custom-toolbar flex flex-row w-full items-center justify-between mb-[2rem]">
      <div className="flex flex-col">
        <h2 className={`text-3xl md:text-3xl font-semibold`}>Calendario</h2>
        <div className="text-sm md:text-base text-gray-500">
          <span className="mr-2">Settimana del</span>
          <span>{moment(toolbar.date).format("DD MMMM YYYY")}</span>
        </div>
      </div>

      <div className="flex flex-row items-center justify-center">
        <ButtonSecondary className="mr-2" onClick={goToPrev}>
          <ArrowLeftIcon width={20} height={20} />
        </ButtonSecondary>
        <ButtonSecondary onClick={goToNext} className="mr-2">
          <ArrowRightIcon width={20} height={20} />
        </ButtonSecondary>
        <ButtonSecondary
          onClick={() =>
            view === "three" ? changeViewToWeek() : changeViewTo3Days()
          }
        >
          {view === "week" ? "Settimana" : "3 giorni"}
        </ButtonSecondary>
      </div>
    </div>
  );
};

const getDayProps = (date: Date) => {
  return {
    style: {
      backgroundColor: moment(date).isSame(moment(), "day")
        ? "#FFFAF0"
        : "white", // Cambia il colore di sfondo per il giorno corrente
    },
  };
};

const MyCustomHeader = ({ date, label }) => {
  const bookings: BookingDataType[] = useSelector(selectBookings);
  return (
    <div>
      <h2>{label}</h2>
      <div>
        {bookings?.map((booking: BookingDataType, index: number) => {
          const bookingStartDay = moment(booking.startDate).startOf("day");
          // For holiday packages, the authoritative duration lives on the item
          // (booking.item.duration_days from the DB). Fall back to endDate otherwise.
          const bookingEndDay =
            booking.type === StockEnum.HOLIDAY_PACKAGE
              ? moment(booking.startDate)
                  .add(
                    (booking?.item as { duration_days?: number })
                      ?.duration_days ?? 1,
                    "days",
                  )
                  .subtract(1, "milliseconds")
              : moment(booking.endDate).endOf("day");

          if (
            moment(date).isBetween(
              bookingStartDay,
              bookingEndDay,
              undefined,
              "[]",
            )
          ) {
            return (
              <div key={index} className="custom-header-event">
                <span>
                  {" "}
                  {booking?.item?.name.slice(0, 35)}{" "}
                  {booking?.item?.name.length >= 35 && "..."}
                </span>
              </div>
            );
          } else {
            return null;
          }
        })}
      </div>
    </div>
  );
};
const EventComponent = ({ event }) => {
  const dispatch = useDispatch();
  const events = useSelector(selectEvents);

  const handleRemoveEvent = () => {
    const filteredEvents = events.filter(
      (e) => e?.experience_uuid !== event.experience_uuid,
    );
    dispatch(setEvents(filteredEvents));
  };

  return (
    <div style={{}}>
      {event.name}
      {event.experience_uuid && (
        <div onClick={handleRemoveEvent} className="absolute top-2 right-2">
          <XMarkIcon width={20} height={20} />
        </div>
      )}
    </div>
  );
};

const CustomCalendar = ({
  events,
  defaultDate,
  onNavigate,
  checkIfSlotsIsAvailable,
}) => {
  // get events from redux store
  const dispatch = useDispatch();
  const bookings: BookingDataType[] = useSelector(selectBookings);
  const [draggedEvent, setDraggedEvent] = useState<any | null>(null);

  const displayEvents = [
    ...bookings
      .filter((booking: BookingDataType) => booking.type === "experience")
      .map((booking: BookingDataType) => ({
        ...booking.item,
        id: booking.id,
        name: booking.item.name,
        title: booking.item.name,
        start: moment(booking.startDate).toDate(),
        end: moment(booking.endDate).toDate(),
        isBooking: true,
      })),
    ...events,
  ];

  const dayFormat = (date, culture, localizer) =>
    localizer.format(date, "D MMM", culture);

  const moveEvent = async ({ event, start, end }) => {
    if (event.isBooking) {
      setDraggedEvent(null);
      return;
    }

    const idx = events.findIndex((existingEvent) => {
      if (event.id && existingEvent.id) {
        return existingEvent.id === event.id;
      }

      return (
        existingEvent.experience_uuid === event.experience_uuid &&
        moment(existingEvent.start).isSame(moment(event.start)) &&
        moment(existingEvent.end).isSame(moment(event.end))
      );
    });

    if (idx === -1) {
      setDraggedEvent(null);
      return;
    }

    // get hours from start and end
    const startHours = moment()
      .hours(start.getHours())
      .minutes(start.getMinutes());
    const endHours = moment().hours(end.getHours()).minutes(end.getMinutes());

    const validInterval = await isValidInterval(
      event,
      event.available_from,
      event.available_to,
      startHours,
      endHours,
      event.minutes_duration,
      event.start,
      event.end,
    );
    // Verifica se il nuovo intervallo (start-end) è valido
    if (validInterval === false) {
      setDraggedEvent(null);
      alert(
        "Errore: l'evento non può essere spostato al di fuori degli intervalli disponibili",
      );
      return;
    }

    const updatedEvent = { ...events[idx], start, end };
    const nextEvents = [...events];
    nextEvents.splice(idx, 1, updatedEvent);

    dispatch(setEvents(nextEvents));
    setDraggedEvent(null);
  };

  const isValidInterval = async (
    experience,
    availableFrom,
    availableTo,
    startDate,
    endDate,
    minutesDuration,
    requestedCheckin,
    requestedCheckout,
  ) => {
    // Calcola la fine degli slot orari in base all'intervallo desiderato
    const slotDuration = moment.duration(minutesDuration, "minutes");
    const availableStart = moment().hours(availableFrom).startOf("hour");
    const availableEnd = moment().hours(availableTo).startOf("hour");
    // Inizializza gli orari di inizio e fine dell'intervallo selezionato

    // Controlla che l'inizio e la fine dell'intervallo selezionato corrispondano agli inizi e alle fine degli slot orari desiderati
    if (
      !startDate.isSameOrAfter(availableStart) ||
      !startDate.isSameOrBefore(availableEnd)
    ) {
      return false;
    }

    // if startDate does not start at slotDuration or startDate is not at the start of the hour if slotDuration is 60
    if (
      startDate.minutes() % slotDuration.asMinutes() !== 0 ||
      (slotDuration.asMinutes() === 60 && startDate.minutes() !== 0)
    ) {
      return false;
    }

    const slots = [];
    let currentSlot = availableStart.clone();
    while (currentSlot.isSameOrBefore(availableEnd)) {
      slots.push(currentSlot.hours());
      currentSlot.add(slotDuration);
    }

    // Check if the selected start and end date are in the slots
    if (
      !slots.includes(startDate.hours()) ||
      !slots.includes(endDate.hours())
    ) {
      return false;
    }

    const experienceToValidate = {
      ...experience,
      requestedCheckin,
      requestedCheckout,
    };
    const checkAvailable = await checkIfSlotsIsAvailable(
      experienceToValidate,
      startDate.hours(),
      endDate.hours(),
    );
    return checkAvailable.available;
  };

  const validSlotKeys = useMemo(() => {
    if (
      !draggedEvent ||
      draggedEvent.available_from === undefined ||
      draggedEvent.available_to === undefined ||
      !draggedEvent.minutes_duration
    ) {
      return new Set<string>();
    }

    const slotDuration = Math.max(
      1,
      Number(draggedEvent.minutes_duration) || 60,
    );
    const startHour = Number(draggedEvent.available_from);
    const endHour = Number(draggedEvent.available_to);
    const durationMinutes = slotDuration;

    const keys = new Set<string>();

    if (Number.isNaN(startHour) || Number.isNaN(endHour)) {
      return keys;
    }

    const weekStart = moment(defaultDate).startOf("week");
    const weekEnd = moment(defaultDate).endOf("week");

    for (
      const currentDay = weekStart.clone();
      currentDay.isSameOrBefore(weekEnd, "day");
      currentDay.add(1, "day")
    ) {
      for (
        let totalMinutes = startHour * 60;
        totalMinutes + durationMinutes <= endHour * 60;
        totalMinutes += slotDuration
      ) {
        const slotMoment = currentDay
          .clone()
          .startOf("day")
          .add(totalMinutes, "minutes");
        keys.add(slotMoment.format("YYYY-MM-DD-HH-mm"));
      }
    }

    return keys;
  }, [draggedEvent, defaultDate]);

  const slotPropGetter = (date: Date) => {
    if (!draggedEvent) {
      return {};
    }

    const slotKey = moment(date).format("YYYY-MM-DD-HH-mm");
    if (validSlotKeys.has(slotKey)) {
      return {
        className: "rbc-valid-drop-slot",
      };
    }

    return {
      className: "rbc-invalid-drop-slot",
    };
  };

  if (!displayEvents) return null;
  return (
    <div className="nc-calendar-page">
      <div className="h-[auto] mx-auto px-10 shadow-lg rounded-lg mb-10 pb-10">
        {displayEvents && (
          <DnDCalendar
            defaultDate={defaultDate}
            onNavigate={(date) => onNavigate(date)}
            defaultView="week" // Imposta la vista predefinita a 'day' (giorno)
            views={{
              day: true,
              week: true,
            }}
            events={displayEvents}
            resizable={false}
            style={{ height: "100vh" }}
            min={moment().startOf("day").add(6, "hours").toDate()}
            max={moment().startOf("day").add(21, "hours").toDate()}
            culture="it"
            formats={{
              dayFormat,
              timeGutterFormat: "HH:mm", // Imposta il formato delle ore in 24
            }}
            onEventDrop={moveEvent}
            onDragStart={({ event }) =>
              setDraggedEvent(
                (event as { isBooking?: boolean }).isBooking ? null : event,
              )
            }
            onDragOver={() => {}}
            onSelectSlot={() => setDraggedEvent(null)}
            onSelectEvent={() => setDraggedEvent(null)}
            onDropFromOutside={() => setDraggedEvent(null)}
            components={{
              toolbar: MyCustomToolbar, // Usa il tuo componente personalizzato per la toolbar
              event: EventComponent,
              header: MyCustomHeader,
            }}
            dayPropGetter={getDayProps}
            slotPropGetter={slotPropGetter}
            localizer={localizer}
          />
        )}
      </div>
    </div>
  );
};
export default CustomCalendar;
